import { getMenuItems } from '@countries/rs/app/modules/userProfile/menuItems';
import {
    ShellLocalConfig,
    SiteLingualityKind,
} from '@src/config/shell/localConfigTypes';
import {
    NavigationItem,
    NavigationKind,
} from '@app/router/types';
import { RouteName } from '@app/router/constants/RouteName';
import {
    GamingRoutes,
    SocialRoutes,
    TicketRoutes,
    Locale,
} from '@superbet-group/web.types';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import { CustomGamingRoutes } from '@gamingFederation/customRoutes';
import { WikiPath } from '@modules/wiki/WikiPath';
import {
    DepositType,
    WithdrawalType,
} from '@core/models/user/enums';
import { BonusSubtypeDto } from '@src/app/promotionHub/bonuses/types/betlerDto/BonusSubtypeDto';
import { isoCode2ToIsoCode } from '@sportOfferFederation/flags/flagsDefinitions';

export const shellLocalConfig: Partial<ShellLocalConfig> = {
    responsibleGamingPageElements: {
        DepositLimit: true,
        LossLimit: true,
        SessionLimit: true,
        RealityCheck: false,
        TotalWagerLimit: true,
        SelfExclusion: true,
        TimeOut: true,
        AccountClosure: true,
    },
    footer: {
        viberNumber: '+381 69 777 115',
        hasLegalSectionTitle: false,
        hasAgeWarning: true,
        hasDisclaimer: true,
        hasOnjnLogo: false,
        hasRombetLogo: false,
        hasIbiaLogo: false,
        hasJocResponsabilLogo: false,
        hasBigLogos: false,
        hasEighteenPlus: true,
        hasEighteenPlusCrossed: true,
        hasEighteenPlusNoLink: false,
        hasMgaLogo: false,
        hasBrazilGovLogo: false,
        hasBagoLogo: false,
        hasFpsLogo: false,
        hasGamblingHelpLogo: false,
        hasAlwaysPlayLegallyLogo: false,
        hasPlayResponsiblyText: false,
        hasGamblingTherapyLogo: false,
        hasResponsibleGamingLinks: false,
    },
    bonusWallet: {
        awardFreeSpinsByCount: true,
        categories: {
            CardGames: true,
            Sport: true,
            Virtuals: false,
            Casino: true,
            Retail: true,
            Bingo: false,
        },
    },
    showFooterRightsReserved: true,
    getAccountSidebarMenuItems: getMenuItems,
    countryCodesWhitelist: ['RS'],
    hasImprovedWithdrawalError: true,
    hasImprovedDepositError: true,
    isUsingErrorCodesTranslations: true,
    isUsingHistoryFrontendTranslations: true,
    isSportTaxVisible: false,
    hasSocialFederation: true,
    deposit: {
        isAcceptingTermMandatory: true,
    },
    hasEnforcedAmountDecimals: true,
    superSpinGameLaunch: true,
    navigationDestinations: {
        [NavigationItem.Logo]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.App.Home,
            },
        },
        [NavigationItem.Sport]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.Sport.PrematchOffer,
            },
        },
        [NavigationItem.Live]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.Sport.CategoryPerTime,
                params: {
                    timeFilter: TimeFilterSlug.Live,
                },
            },
        },
        [NavigationItem.Casino]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: GamingRoutes.MyCasinoHome,
            },
        },
        [NavigationItem.CasinoLive]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: GamingRoutes.MyCasinoLive,
            },
        },
        [NavigationItem.Virtual]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: GamingRoutes.MyCasinoVirtuals,
            },
        },
        [NavigationItem.Promotions]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.GrowthMarketing.PromotionsInfo,
            },
        },
        [NavigationItem.Help]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.App.Wiki,
                params: {
                    pageSlug: WikiPath.Help,
                },
            },
        },
        [NavigationItem.VerifyTicket]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: TicketRoutes.VerifyTicket,
            },
        },
        [NavigationItem.Social]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: SocialRoutes.Home,
            },
        },
        [NavigationItem.MyTickets]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: TicketRoutes.Tickets,
            },
        },
        [NavigationItem.News]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.News.Feed,
            },
        },
    },
    bottomNavbarItems: [
        NavigationItem.Sport,
        NavigationItem.Live,
        NavigationItem.Casino,
        NavigationItem.MyTickets,
    ],
    siteLanguageConfig: {
        kind: SiteLingualityKind.SingleLingual,
        locales: [
            {
                name: 'RS',
                value: Locale.Serbian,
                flag: isoCode2ToIsoCode.RS,
            },
            {
                name: 'EN',
                value: Locale.English,
                flag: isoCode2ToIsoCode.GB,
            },
        ],
    },
    urlLocale: Locale.Serbian,
    isCompleteAccountActivationModalEnabled: true,
    shouldUseUserUuidForInbox: true,
    myCasinoRouteNames: [
        CustomGamingRoutes.MyCasinoLiveCasinoCategory,
    ],
    defaultDepositTypes: {
        [DepositType.Online]: true,
        [DepositType.OnlinePix]: false,
        [DepositType.Aircash]: false,
        [DepositType.BankTransfer]: true,
        [DepositType.Shop]: false,
        [DepositType.PaySafeCard]: false,
        [DepositType.PaySpot]: true,
        [DepositType.Transaks]: false,
        [DepositType.Bancontact]: false,
        [DepositType.PaymentIQ]: false,
    },
    defaultWithdrawalTypes: {
        [WithdrawalType.Online]: false,
        [WithdrawalType.OnlinePix]: false,
        [WithdrawalType.BankTransfer]: true,
        [WithdrawalType.Instant]: false,
        [WithdrawalType.Shop]: false,
        [WithdrawalType.PaySafeCard]: false,
        [WithdrawalType.Transaks]: false,
        [WithdrawalType.PaymentIQ]: false,
    },
    isSportalNewsEnabled: false,
    freeToPlay: {
        useEligibilityChecker: true,
    },
    bonus: {
        excludedBonusTypes: [BonusSubtypeDto.CashBonus],
        isPromotionHubEntryPointVisible: true,
    },
    sponsoredEntities: [
        {
            name: 'Mega Basket',
            imageSrc: 'serbia-sponsorship-megabasket',
            url: 'https://www.bcmegabasket.net',
        },
    ],
    hasSponsorshipSubtitle: false,
    isUsernameDisplayed: true,
    failedLoginAttemptsBeforeForgotPassword: 2,
};
